/*
 * Stable Date Filter Input
 *
 * Features:
 * - Implements AG Grid's custom date selection component contract
 * - Preserves native browser date picking, validation constraints, and accessibility hooks
 * - Reports only committed date changes so calendar month navigation stays open
 *
 * Implementation:
 * - Uses local YYYY-MM-DD serialization to avoid timezone drift
 * - Mirrors AG Grid input structure and classes for theme compatibility
 * - Deliberately ignores intermediate native input events
 *
 * Changes:
 * - 2026-08-04: Created to prevent account date filters from closing during month navigation
 */

import type { IAfterGuiAttachedParams, IDateComp, IDateParams } from 'ag-grid-community';

function padDatePart(value: number): string {
  return String(value).padStart(2, '0');
}
function serializeLocalDate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

function parseLocalDate(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue) - 1;
  const day = Number(dayValue);
  const date = new Date(year, month, day);

  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function serializeBoundary(date: Date | string | undefined, year: number | undefined, isMinimum: boolean): string {
  if (date instanceof Date) {
    return serializeLocalDate(date);
  }

  if (typeof date === 'string') {
    return date;
  }

  if (year !== undefined) {
    return `${year}-${isMinimum ? '01-01' : '12-31'}`;
  }

  return '';
}

export class DateFilterInput implements IDateComp {
  private readonly gui: HTMLDivElement;
  private readonly input: HTMLInputElement;
  private params: IDateParams | null = null;

  constructor() {
    this.gui = document.createElement('div');
    this.gui.className = 'ag-filter-filter';

    const field = document.createElement('div');
    field.className = 'ag-input-field ag-text-field ag-date-filter';
    field.setAttribute('role', 'presentation');

    const label = document.createElement('div');
    label.className = 'ag-input-field-label ag-text-field-label';

    const wrapper = document.createElement('div');
    wrapper.className = 'ag-wrapper ag-input-wrapper ag-text-field-input-wrapper';
    wrapper.setAttribute('role', 'presentation');

    this.input = document.createElement('input');
    this.input.type = 'date';
    this.input.autocomplete = 'off';
    this.input.className = 'ag-input-field-input ag-text-field-input';
    this.input.addEventListener('change', this.handleCommittedChange);
    this.input.addEventListener('focusin', this.handleFocusIn);

    wrapper.appendChild(this.input);
    field.append(label, wrapper);
    this.gui.appendChild(field);
  }

  init(params: IDateParams): void {
    this.setParams(params);
  }

  refresh(params: IDateParams): void {
    this.setParams(params);
  }

  getGui(): HTMLElement {
    return this.gui;
  }

  getDate(): Date | null {
    return parseLocalDate(this.input.value);
  }

  setDate(date: Date | null): void {
    this.input.value = serializeLocalDate(date);
  }

  setDisabled(disabled: boolean): void {
    this.input.disabled = disabled;
    this.input.closest('.ag-input-field')?.classList.toggle('ag-disabled', disabled);
  }

  setInputPlaceholder(placeholder: string): void {
    this.input.placeholder = placeholder;
  }

  setInputAriaLabel(ariaLabel: string): void {
    this.input.setAttribute('aria-label', ariaLabel);
  }

  afterGuiAttached(params?: IAfterGuiAttachedParams): void {
    if (!params?.suppressFocus) {
      this.input.focus({ preventScroll: true });
    }
  }

  destroy(): void {
    this.input.removeEventListener('change', this.handleCommittedChange);
    this.input.removeEventListener('focusin', this.handleFocusIn);
    this.params = null;
  }

  private readonly handleCommittedChange = (): void => {
    if (!this.input.disabled) {
      this.params?.onDateChanged();
    }
  };

  private readonly handleFocusIn = (): void => {
    this.params?.onFocusIn?.();
  };

  private setParams(params: IDateParams): void {
    this.params = params;

    const { minValidDate, maxValidDate, minValidYear, maxValidYear } = params.filterParams;
    const minimum = serializeBoundary(minValidDate, minValidYear, true);
    const maximum = serializeBoundary(maxValidDate, maxValidYear, false);

    if (minimum) {
      this.input.min = minimum;
    } else {
      this.input.removeAttribute('min');
    }

    if (maximum) {
      this.input.max = maximum;
    } else {
      this.input.removeAttribute('max');
    }
  }
}
